import React, { useEffect, useState } from "react";
import {
    Box,
    Grid,
    Typography,
    Paper,
    IconButton,
    Chip,
    CircularProgress,
    Tooltip,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import WSSelectField from "../../../Components/Select/WSSelect";
import WSTextField, { TextType } from "../../../Components/TextBox/WSTextBox";
import { callAPI, Method, fetchAllPaginated } from "../../../commonFunctions/CommonFunctions";
import { useSnackbar } from "../../../Components/Snackbar/SnackBarContext";
import ReasonModal from "./ReasonModal";

// ✅ TypeScript Interfaces
interface Item {
    ItemId: string;
    ItemName: string;
    ItemCode: string;
    IsActive: boolean;
}

interface Warehouse {
    WarehouseId: string;
    WarehouseName: string;
    Location: string;
    IsActive: boolean;
}

interface AdjustmentReason {
    ReasonId: string;
    ReasonName: string;
    IsActive: boolean;
}

interface StockAdjustmentData {
    ItemId: string;
    WarehouseId: string;
    PhysicalQty: number;
    ReasonId: string;
}

const StockAdjustment = () => {
    const [loading, setLoading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [selectedId, setSelectedId] = useState<string | number>(0);
    const [openModal, setOpenModal] = useState(false);
    const { showMessage } = useSnackbar();
    const params = useParams();
    const id = params.id;
    const navigate = useNavigate();

    // ✅ Properly typed state
    const [items, setItems] = useState<Item[]>([]);
    const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
    const [reasons, setReasons] = useState<AdjustmentReason[]>([]);

    const {
        control,
        handleSubmit,
        reset,
        formState: { isValid },
    } = useForm({
        mode: "onChange",
        defaultValues: {
            ItemId: "",
            WarehouseId: "",
            PhysicalQty: "",
            ReasonId: "",
        },
    });


    const fetchAllData = (url: string) => fetchAllPaginated(url);

    // Fetch dropdown data
   const fetchDropdownData = async () => {
    try {
        setLoading(true);

        const [
            allItems,
            allWarehouses,
            allReasons
        ] = await Promise.all([
            fetchAllData("items/list"),
            fetchAllData("warehouses/list"),
            fetchAllData("stock-adjustment/reason"),
        ]);

        setItems(allItems);
        setWarehouses(allWarehouses);
        setReasons(allReasons);

    } catch (error) {
        console.error("Failed to fetch dropdown data:", error);
        showMessage("Failed to load dropdown data", "error");
    } finally {
        setLoading(false);
    }
};

    useEffect(() => {

        if (!id || id === "undefined" || id === "null" || id.trim() === "") {
            setIsEditMode(false);
            reset();
            fetchDropdownData();
            return;
        }

        setIsEditMode(true);
        fetchStockAdjustmentData(id);
        fetchDropdownData();
    }, [id, reset]);

    const fetchStockAdjustmentData = async (adjustmentId: string) => {
        try {
            setLoading(true);
            const response = await callAPI(`stock-adjustment/${adjustmentId}`, Method.GET);
            
            if (response?.success && response.data) {
                const adjustmentData: StockAdjustmentData = response.data;
                reset({
                    ItemId: adjustmentData.ItemId || "",
                    WarehouseId: adjustmentData.WarehouseId || "",
                    PhysicalQty: adjustmentData.PhysicalQty?.toString() || "",
                    ReasonId: adjustmentData.ReasonId || "",
                });
            } else {
                showMessage(response?.message || `Stock adjustment ${adjustmentId} not found`, "error");
                setIsEditMode(false);
            }
        } catch (error: any) {
            console.error("❌ Fetch error:", error.response?.data || error.message);
            showMessage("Failed to load stock adjustment data", "error");
            setIsEditMode(false);
        } finally {
            setLoading(false);
        }
    };

    const onSubmit = async (data: any) => {
        try {
            setLoading(true);
            const requestBody: StockAdjustmentData = {
                ItemId: data.ItemId || "",
                WarehouseId: data.WarehouseId || "",
                PhysicalQty: parseInt(data.PhysicalQty) || 0,
                ReasonId: data.ReasonId || "",
            };

            let response;
            if (isEditMode && id) {
                response = await callAPI(`stock-adjustment/${id}`, Method.PATCH, requestBody);
            } else {
                response = await callAPI("stock-adjustment/", Method.POST, requestBody);
            }

            if (response?.success) {
                showMessage(
                    isEditMode ? "Stock adjustment updated successfully!" : "Stock adjustment created successfully!",
                    "success"
                );
                navigate("/adjustment/list");
            } else {
                showMessage(response?.message || "Operation failed", "error");
            }
        } catch (error: any) {
            showMessage(error?.message || "Operation failed", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleAddReasonClick = () => {
        setOpenModal(true);
    };

    const handleAddNewReason = async (reasonName: string) => {
        try {
            const response = await callAPI("stock-adjustment/reason", Method.POST, {
                ReasonName: reasonName,
            });

            if (response?.success) {
                const newReason: AdjustmentReason = response.data;
                setReasons(prev => [...prev, newReason]);
                showMessage("Reason added successfully!", "success");
            }
        } catch (error: any) {
            showMessage(error?.message || "Failed to add reason", "error");
        }
        setOpenModal(false);
    };

    return (
        <Box sx={{ p: 2, background: "#f4f4f4", minHeight: "100vh" }}>
            <Paper sx={{ borderRadius: "16px", p: 3, background: "#fff" }}>
                
                {}
                <Box display="flex" justifyContent="space-between" mb={5}>
                    <Typography sx={{ fontSize: "18px", fontWeight: 600 }}>
                        Stock Adjustment
                    </Typography>

                    <Tooltip title={isEditMode ? "Update stock adjustment" : "Create stock adjustment"} arrow>
                               <IconButton
                                 sx={{ background: "#000", color: "#fff" }}
                                 onClick={handleSubmit(onSubmit)}
                                 disabled={!isValid || loading}
                               >
                                 <SaveIcon fontSize="small" />
                               </IconButton>
                             </Tooltip>
                </Box>

                {loading && (
                    <Box sx={{
                        position: "absolute",
                        top: 0, left: 0, right: 0, bottom: 0,
                        background: "rgba(255,255,255,0.9)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        zIndex: 10, borderRadius: "16px"
                    }}>
                        <CircularProgress />
                    </Box>
                )}

                <Grid container rowSpacing={7} columnSpacing={5}>
                    {}
                    <Grid item xs={4}>
                        <WSSelectField
                            name="ItemId"
                            label="Item Name"
                            id="ItemId"
                            control={control}
                            inlineSearch
                            options={items.map(item => ({
                                value: item.ItemId,
                                label: `${item.ItemCode} - ${item.ItemName}`
                            }))}
                        />
                    </Grid>
                    <Grid item xs="auto" display="flex" alignItems="flex-start" sx={{ pl: '12px !important' }}>
  <Box
   onClick={() =>
  navigate("/adjustment/itemmaster", { state: { from: "adjustment" } })
}
    sx={{
      height: "36px",
      width: "36px",
      borderRadius: "10px",
      background: "#f3f3f3",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      flexShrink: 0,
      border: "1px solid #d1d5db",
      "&:hover": {
        background: "#e0e0e0",
      },
    }}
  >
    <AddIcon fontSize="small" />
  </Box>
</Grid>

                    {}
                    <Grid item xs={4}>
                        <WSSelectField
                            name="WarehouseId"
                            label="Warehouse"
                            id="WarehouseId"
                            control={control}
                            inlineSearch
                            options={warehouses.map(warehouse => ({
                                value: warehouse.WarehouseId,
                                label: warehouse.WarehouseName
                            }))}
                        />
                    </Grid>

                     <Grid item xs="auto" display="flex" alignItems="flex-start" sx={{ pl: '12px !important' }}>
  <Box
   onClick={() =>
  navigate("/adjustment/warehouse", { state: { from: "adjustment" } })
}
    sx={{
      height: "36px",
      width: "36px",
      borderRadius: "10px",
      background: "#f3f3f3",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      flexShrink: 0,
      border: "1px solid #d1d5db",
      "&:hover": {
        background: "#e0e0e0",
      },
    }}
  >
    <AddIcon fontSize="small" />
  </Box>
</Grid>

                    {}
                    <Grid item xs={4}>
                        <WSTextField
                            name="PhysicalQty"
                            label="Physical Quantity"
                            id="PhysicalQty"
                            control={control}
                            type={TextType.AllowOnlyNumber}
                        />
                    </Grid>

                    {}
                    <Grid item xs={4}>
                        <WSSelectField
                            name="ReasonId"
                            label="Adjustment Reason"
                            id="ReasonId"
                            control={control}
                            inlineSearch
                            options={reasons.map(reason => ({
                                value: reason.ReasonId,
                                label: reason.ReasonName
                            }))}
                        />

                        {}
                        <Box
                            sx={{
                                mt: 3,
                                px: 2.5,
                                py: 1.75,
                                borderRadius: "12px",
                                background: "#f3f3f3",
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                                flexWrap: "wrap",
                            }}
                        >
                            {reasons.map((r) => (
                                <Chip key={r.ReasonId} label={r.ReasonName} />
                            ))}

                            <Box
                                onClick={handleAddReasonClick}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    cursor: "pointer",
                                    color: "#333",
                                    py: 0.5,
                                }}
                            >
                                <AddIcon fontSize="small" />
                                <Typography sx={{ ml: 1, fontSize: "13px" }}>
                                    Add New Reason
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {}
            <ReasonModal
                open={openModal}
                onClose={() => setOpenModal(false)}
                onSave={handleAddNewReason}
            />
        </Box>
    );
};

export default StockAdjustment;